import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { config } from "@/shared/lib/config";
import { fetcher } from "@/shared/lib/fetcher";

export type DisputeListItem = {
  id: string;
  type: string;
  referenceId: string | null;
  openedBy: string;
  openedByName: string | null;
  openedByImage: string | null;
  status: string;
  reason: string | null;
  createdAt: string;
};

export type ListDisputesResult = {
  data: DisputeListItem[];
  meta: { total: number; limit: number; offset: number };
};

export type ListDisputesParams = OffsetPageQueryDTO;

export type OrderItemSummary = {
  productName: string | null;
  sku: string | null;
  qty: number;
  unitPrice: number;
};

export type OrderPreview = {
  orderId: string;
  total: number;
  currency: string;
  status: string;
  customerName: string | null;
  customerPhone: string | null;
  deliveryAddress: string | null;
  shopName: string | null;
  shopPhone: string | null;
  shopAddress: string | null;
  shopCity: string | null;
  items: OrderItemSummary[];
};

export type JobItemSummary = {
  productName: string | null;
  sku: string | null;
  qty: number;
  unitPrice: number;
};

export type JobPreview = {
  jobId: string;
  title: string;
  status: string;
  customerName: string | null;
  customerPhone: string | null;
  location: string | null;
  city: string | null;
  mechanicName: string | null;
  mechanicDisplayName: string | null;
  items: JobItemSummary[];
  laborFee: number;
  currency: string;
  total: number;
};

export type DisputeDetail = {
  id: string;
  type: string;
  referenceId: string | null;
  openedBy: string;
  openedByName: string | null;
  status: string;
  reason: string | null;
  resolution: string | null;
  createdAt: string;
  resolvedAt: string | null;
  orderPreview: OrderPreview | null;
  jobPreview: JobPreview | null;
};

export type UpdateDisputeStatusPayload =
  | { status: "investigating" }
  | { action: "resolved" | "rejected"; resolution: string };

export type DisputesSummary = {
  responseTimeAvgHours: number | null;
  openCases: number;
  resolutionRatePercent: number;
  blockedOrders: number;
};

export const disputesApi = {
  async getSummary() {
    return fetcher.get<DisputesSummary>(`${config.apiUrl}/disputes/summary`);
  },

  async listDisputes(params: ListDisputesParams) {
    const url = new URL(`${config.apiUrl}/disputes`);
    url.searchParams.set("limit", String(params.limit ?? 20));
    url.searchParams.set("offset", String(params.offset ?? 0));
    if (params.sort) url.searchParams.set("sort", JSON.stringify(params.sort));
    if (params.filters)
      url.searchParams.set("filters", JSON.stringify(params.filters));
    return fetcher.get<ListDisputesResult>(url.toString());
  },

  async getDispute(id: string) {
    return fetcher.get<DisputeDetail>(`${config.apiUrl}/disputes/${id}`);
  },

  async updateDisputeStatus(id: string, payload: UpdateDisputeStatusPayload) {
    return fetcher.patch<{ ok: boolean }>(
      `${config.apiUrl}/disputes/${id}/status`,
      payload,
    );
  },
};
